import _ from 'lodash';
import { adminClient as apolloClient } from '@/store/apollo';

import AuthApi from '@/store/api/system/auth';

const PREVIEW_ENDPOINT = `${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PREVIEW_ROUTE}`;

import EventsGql from '@/extensions/eventManager/store/graphql/queries/Events.gql';
import EventGql from '@/extensions/eventManager/store/graphql/queries/Event.gql';
import EventsPaginatedGql from '@/extensions/eventManager/store/graphql/queries/EventsPaginated.gql';
import CreateEventGql from '@/extensions/eventManager/store/graphql/mutations/CreateEvent.gql';
import UpdateEventGql from '@/extensions/eventManager/store/graphql/mutations/UpdateEvent.gql';
import DeleteEventGql from '@/extensions/eventManager/store/graphql/mutations/DeleteEvent.gql';
import DetachTicketProductGql from '@/extensions/eventManager/store/graphql/mutations/DetachTicketProduct.gql';

/**
 * Drop fields the event read fragment carries but the input types do not accept.
 *
 * The whole form is sent back on save, so a read-only field on a slot or booking makes the entire
 * mutation invalid: "Field X is not defined by type EX_EVNT_SlotInput". `ticketProducts` is derived
 * from the articles, and `qr_token` is deliberately not writable - a token is minted server-side.
 */
// Antworten auf das Anmeldeformular: gelesen kommen sie als { label, value, field { id } },
// die Mutation liest je Eintrag field.id und value. Gesendet werden sie nur für neue Buchungen
// und für solche, deren Antworten im Modal geändert wurden (valuesChanged). Sonst würde jedes
// Speichern des Events alle alten Antworten neu gegen ein evtl. geändertes Template prüfen.
const toBookingValuesInput = booking => {
    // `answers` ist nur der Modal-Zustand (Antworten je Feld-ID), kein Feld des Booking-Inputs.
    booking = _.omit(booking, ['answers']);

    const isNew = _.startsWith(String(booking.id), 'new:');
    if (!isNew && !booking.valuesChanged) return _.omit(booking, ['values', 'valuesChanged']);

    const values = (booking.values || [])
        .filter(value => _.get(value, 'field.id'))
        .map(value => ({
            field: { id: value.field.id },
            value: value.value,
        }));

    return { ..._.omit(booking, ['valuesChanged']), values };
};

const stripReadOnlyFields = event => {
    const slots = (event.slots || []).map(slot => {
        const bookings = (slot.bookings || []).map(booking => toBookingValuesInput(_.omit(booking, ['qr_token'])));

        return { ..._.omit(slot, ['ticketProducts']), ...(slot.bookings ? { bookings } : {}) };
    });

    return event.slots ? { ...event, slots } : event;
};

export default {
    fetchList: (cmsID = null) =>
        new Promise((resolve, reject) => {
            let variables = {};
            if (cmsID) variables.cms = parseInt(cmsID);

            apolloClient
                .query({
                    query: EventsGql,
                    variables
                })
                .then(({ data }) => resolve(data.EX_EVNT_Events))
                .catch(err => reject(err));
        }),
    fetchEventsPaginated: data =>
        new Promise((resolve, reject) => {
            let filters = [];
            _.each(data.filters, entry => filters.push(entry));

            apolloClient
                .query({
                    query: EventsPaginatedGql,
                    variables: {
                        currentPage: _.get(data, 'pageInfo.currentPage', 1),
                        perPage: _.get(data, 'pageInfo.perPage', 25),
                        order: _.get(data, 'variables.order', 'id'),
                        direction: _.get(data, 'variables.direction', 'asc'),
                        link: _.get(data, 'link.route', '/event-manager/event/'),
                        filters
                    }
                })
                .then(({ data }) => resolve(data.EX_EVNT_EventsPaginated))
                .catch(err => reject(err));
        }),
    // Takes a ticket product back off one date: the slot takes free registrations again, the
    // product and the tickets already sold stay.
    detachTicketProduct: (slotId, productId) => {
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: DetachTicketProductGql,
                    variables: { slot: parseInt(slotId), product: parseInt(productId) },
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_EVNT_DetachTicketProduct);
                })
                .catch(err => reject(err));
        });
    },

    fetchEvent: id => {
        if (id === undefined) throw new Error('required argument is missing');

        let variables = { id: parseInt(id) };

        return new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: EventGql,
                    variables
                })
                .then(({ data }) => resolve(data.EX_EVNT_Event))
                .catch(err => reject(err));
        });
    },
    create: event => {
        if (event === undefined) throw new Error('required argument is missing');

        event = _.removeDeepByKey(event, '__typename');
        // Nicht-destruktiver Banner-Zuschnitt: nur die Crop-Settings (Fokuspunkt) senden.
        // banner_id trägt die Referenz (Upload-ID bei neuem Upload, sonst bestehende Image-ID),
        // banner_settings den Zuschnitt. null = Zuschnitt entfernen (Original).
        const bannerProcessing = _.get(event, 'banner.settings.processing');
        event.banner_settings = bannerProcessing
            ? { processing: _.pick(bannerProcessing, ['fit', 'crop', 'x', 'y', 'z', 'w', 'h']) }
            : null;
        // banner_id immer aus dem aktuellen Banner ableiten: beim geladenen Event ist nur
        // `banner.id` (Image-ID) vorhanden, kein top-level banner_id – sonst überspringt
        // die Mutation den Banner-Block und der Zuschnitt wird nicht gespeichert.
        // banner.id ist ein GraphQL-ID (String) → nach Int wandeln (Input erwartet Int).
        const rawBannerId = parseInt(_.get(event, 'banner.id') ?? event.banner_id, 10);
        event.banner_id = Number.isNaN(rawBannerId) ? null : rawBannerId;
        event = stripReadOnlyFields(_.omit(event, ['banner']));
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CreateEventGql,
                    variables: { event }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_EVNT_CreateEvent);
                })
                .catch(err => reject(err));
        });
    },
    update: event => {
        if (event === undefined) throw new Error('required argument is missing');

        event = _.removeDeepByKey(event, '__typename');
        // Nicht-destruktiver Banner-Zuschnitt: nur die Crop-Settings (Fokuspunkt) senden.
        // banner_id trägt die Referenz (Upload-ID bei neuem Upload, sonst bestehende Image-ID),
        // banner_settings den Zuschnitt. null = Zuschnitt entfernen (Original).
        const bannerProcessing = _.get(event, 'banner.settings.processing');
        event.banner_settings = bannerProcessing
            ? { processing: _.pick(bannerProcessing, ['fit', 'crop', 'x', 'y', 'z', 'w', 'h']) }
            : null;
        // banner_id immer aus dem aktuellen Banner ableiten: beim geladenen Event ist nur
        // `banner.id` (Image-ID) vorhanden, kein top-level banner_id – sonst überspringt
        // die Mutation den Banner-Block und der Zuschnitt wird nicht gespeichert.
        // banner.id ist ein GraphQL-ID (String) → nach Int wandeln (Input erwartet Int).
        const rawBannerId = parseInt(_.get(event, 'banner.id') ?? event.banner_id, 10);
        event.banner_id = Number.isNaN(rawBannerId) ? null : rawBannerId;
        event = stripReadOnlyFields(_.omit(event, ['banner']));
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: UpdateEventGql,
                    variables: { event }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_EVNT_UpdateEvent);
                })
                .catch(err => reject(err));
        });
    },
    // Anmeldeliste (oder QR-Aushang) als PDF — serverseitig gerendert (PreviewController)
    bookingsPdfUrl: (eventId, options = {}) => {
        const { columns = [], sort = 'name', landscape = false, showQrCode = true, loginEmail = '', qrOnly = false } = options;

        const token = AuthApi.getToken();
        let url = `${PREVIEW_ENDPOINT}event-bookings/${eventId}?token=${token}`;

        if (qrOnly) {
            url += '&only=qr';
        } else {
            if (columns.length) url += `&columns=${columns.join(',')}`;
            if (sort) url += `&sort=${sort}`;
            if (landscape) url += '&landscape=1';
            if (!showQrCode) url += '&qr=0';
        }

        // Vorbelegung der App-Anmeldung im QR-Code (?login=… im Scanner-Link)
        if (loginEmail && (qrOnly || showQrCode)) url += `&login=${encodeURIComponent(loginEmail)}`;

        return url;
    },
    delete: id => {
        if (id === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: DeleteEventGql,
                    variables: { id: parseInt(id) }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_EVNT_DeleteEvent);
                })
                .catch(err => reject(err));
        });
    }
};
